import { COLOR } from 'admin/constants/styles';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TextBox from './TextBox';

export type SuggestItem = {
  key: string;
  label: string;
  note?: string;
};

type Props = {
  syncValue: string;
  suggestions: SuggestItem[];
  onChangeText: (value: string) => void;
  onSelect: (key: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  fullWidth?: boolean;
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const List = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  margin: 2px 0 0;
  padding: 0;
  list-style: none;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid ${COLOR.lightGray};
  border-radius: 3px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const Item = styled.li<{ highlighted: boolean }>`
  padding: 6px 8px;
  cursor: pointer;
  background: ${({ highlighted }) => (highlighted ? '#f0f6ff' : 'none')};
`;

const ItemNote = styled.span`
  margin-left: 8px;
  color: #999;
  font-size: 0.85em;
`;

// Sequence for stable per-instance listbox ids (React 16 has no useId).
let listboxIdSeq = 0;

// A TextBox with a suggestion dropdown. The parent owns the suggestion list
// (typically fetched from an API as the user types) and receives the selected
// item's key via onSelect.
const SuggestTextBox = ({
  syncValue,
  suggestions,
  onChangeText,
  onSelect,
  onBlur,
  ...rest
}: Props) => {
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const listboxIdRef = useRef('');
  if (listboxIdRef.current === '') {
    listboxIdSeq += 1;
    listboxIdRef.current = `suggest-textbox-listbox-${listboxIdSeq}`;
  }
  const listboxId = listboxIdRef.current;
  const open = focused && !dismissed && suggestions.length > 0;

  // Close the dropdown on selection (standard combobox behavior). The parent
  // may keep the picked item in its suggestion array (e.g. static presets
  // derived from the value), so we cannot rely on the list becoming empty.
  const handleSelect = (key: string) => {
    setDismissed(true);
    onSelect(key);
  };

  // Reset the highlight when the suggestion contents change. Keyed on the
  // item keys (not the array reference) because the parent typically rebuilds
  // the array on every render.
  const suggestionsKey = suggestions.map((item) => item.key).join('\n');
  useEffect(() => {
    setHighlighted(-1);
  }, [suggestionsKey]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlighted((i) => (i + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlighted((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        break;
      case 'Enter':
        if (highlighted >= 0) {
          e.preventDefault();
          handleSelect(suggestions[highlighted].key);
        }
        break;
      case 'Escape':
        setDismissed(true);
        break;
      default:
        break;
    }
  };

  return (
    <Wrapper>
      <TextBox
        {...rest}
        syncValue={syncValue}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && highlighted >= 0 ? `${listboxId}-option-${highlighted}` : undefined
        }
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setDismissed(false);
          onChangeText(e.target.value);
        }}
        onFocus={() => {
          setFocused(true);
          setDismissed(false);
        }}
        onBlur={() => {
          setFocused(false);
          onBlur?.();
        }}
        onKeyDown={handleKeyDown}
      />
      {open && (
        <List role="listbox" id={listboxId}>
          {suggestions.map((item, index) => (
            <Item
              key={item.key}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={index === highlighted}
              highlighted={index === highlighted}
              // Use mousedown (not click) so selection wins over the input's blur.
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(item.key);
              }}
              onMouseEnter={() => setHighlighted(index)}
            >
              {item.label}
              {item.note ? <ItemNote>{item.note}</ItemNote> : null}
            </Item>
          ))}
        </List>
      )}
    </Wrapper>
  );
};

export default SuggestTextBox;
